import { useEffect } from "react";

import { initials, metaOf } from "../data/engine";
import type { LibraryState } from "../data/types";
import type { useKagami } from "../state/useKagami";
import { Chip, CoverArt, SignalBar, mutedColor } from "./primitives";

type K = ReturnType<typeof useKagami>;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function DetailDrawer({ k }: { k: K }) {
  const sel = k.selected;

  // Escape closes. The design has no keyboard affordance at all, but a
  // full-screen overlay with no way out but the mouse is worth fixing.
  useEffect(() => {
    if (!sel) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") k.setSelId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel, k]);

  if (!sel) return null;

  const verbs: LibraryState[] = sel.work.kind === "anime"
    ? ["Watching", "Completed", "Planned", "Dropped"]
    : ["Reading", "Completed", "Planned", "Dropped"];

  return (
    <div>
      <div onClick={() => k.setSelId(null)} style={{
        position: "fixed", inset: 0, zIndex: 70,
        background: "color-mix(in srgb, var(--color-neutral-900) 55%, transparent)",
      }} />
      <aside className="k-drawer" role="dialog" aria-modal="true" aria-label={sel.work.title} style={{
        position: "fixed", zIndex: 80, top: 0, right: 0, bottom: 0, width: 440, overflow: "auto",
        background: "var(--color-bg)", boxShadow: "var(--shadow-lg)", padding: "20px 20px 40px",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <CoverArt initials={initials(sel.work.title)} coverPath={sel.work.coverPath} accent fontSize={26}
            style={{ width: 88, height: 118, flex: "none", borderRadius: 8 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <h4 style={{ margin: "0 0 3px", textWrap: "pretty" }}>{sel.work.title}</h4>
            <div style={{ fontSize: 12, color: mutedColor(55) }}>{metaOf(sel.work)}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
              {sel.work.genres.concat(sel.work.moods.map(cap)).map((t) => (
                <span key={t} className="tag tag-neutral">{t}</span>
              ))}
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => k.setSelId(null)} style={{ flex: "none", padding: "4px 9px" }}>
            Close
          </button>
        </div>

        {/* The whole point of the product: the score, and every signal that
            produced it, with its own explanation on hover. */}
        <div style={{
          marginTop: 18, padding: 14, borderRadius: 10,
          background: "var(--color-surface)", boxShadow: "var(--shadow-sm)",
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
            <span style={{ fontFamily: "var(--font-heading)", fontSize: 28, fontWeight: 600, color: "var(--color-accent)" }}>
              {sel.match}%
            </span>
            <span style={{ fontSize: 11.5, color: mutedColor(55) }}>{sel.why}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {sel.signals.map((s) => <SignalBar key={s.key} signal={s} height={5} />)}
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
          <button className="btn btn-primary" onClick={() => k.tuneTo(sel.work.id)}>Tune to this</button>
          {verbs.map((v) => (
            <Chip key={v} on={k.library[sel.work.id] === v} onClick={() => k.setLibraryState(sel.work.id, v)}
              style={{ padding: "7px 11px" }}>{v}</Chip>
          ))}
        </div>

        <h6 style={{ margin: "22px 0 8px", color: mutedColor(55) }}>Similar</h6>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {sel.similar.map((r) => (
            <div key={r.id} className="k-row" onClick={() => { k.setSelId(r.id); }} style={{
              display: "flex", alignItems: "center", gap: 10, padding: 8, borderRadius: 6,
              cursor: "pointer", borderBottom: `1px solid ${mutedColor(8)}`,
            }}>
              <span style={{
                width: 30, height: 40, flex: "none", borderRadius: 4,
                background: "linear-gradient(155deg, var(--color-neutral-800), var(--color-neutral-900))",
              }} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.title}
                </span>
                <span style={{
                  display: "block", fontSize: 10.5, color: mutedColor(45),
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>{r.why}</span>
              </span>
              <span style={{ fontFamily: "var(--font-heading)", fontSize: 12, color: "var(--color-accent)" }}>
                {r.match}%
              </span>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
